import { useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Checkbox, makeStyles } from '@material-ui/core';
import RichTextPreview from '../../../richTextPreview';
import SelectableUserList from '../../../selectableUserList';
import { ReactComponent as CheckedIcon } from '../../../../../assets/icons/check-box-checked.svg';
import { isArray, isArrayEmpty } from '../../../../../utility/helpers';
import {
  isQuestionnaireQuestionOptionSelectedCheck,
  shouldRenderQuestionnaireQuestionOptionSubject,
} from '../../../../../utility/fillSurveyUtils';

const useStyles = makeStyles(
  ({ palette: { primary }, spacing, breakpoints }) => ({
    root: {
      marginBottom: spacing(2),
      '&:last-of-type': {
        marginBottom: 0,
      },
    },
    answerContent: {
      boxSizing: 'border-box',
      border: `1px solid ${primary.bluish6}`,
      borderRadius: 8,
      display: 'flex',
      flexDirection: 'column',
      userSelect: 'none',
      '&.selected': {
        backgroundColor: primary.blue6,
        '&$singleSelectDisabled': {
          backgroundColor: primary.bluish8,
        },
      },
    },
    answerWrapper: {
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'flex-start',
      minWidth: 0,
      padding: spacing(2),
      [breakpoints.up('sm')]: {
        padding: spacing(4),
      },
    },
    answerWrapperGroup: {
      padding: spacing(2, 2, 0, 2),
      [breakpoints.up('sm')]: {
        padding: spacing(4, 4, 0, 4),
      },
    },
    singleSelect: {
      cursor: 'pointer',
    },
    singleSelectDisabled: {
      cursor: 'default',
    },
    checkbox: {
      pointerEvents: 'none',
      boxSizing: 'border-box',
      padding: 0,
      width: 18,
      height: 18,
      marginRight: spacing(1.5),
    },
    checkboxIcon: {
      boxSizing: 'border-box',
      flexShrink: 0,
      width: 18,
      height: 18,
      border: `2px solid ${primary.bluish4}`,
      borderRadius: 4,
    },
    checkedIcon: {
      flexShrink: 0,
      width: 18,
      height: 18,
      '& path': {
        fill: primary.blue1,
      },
    },
    disabledCheckedIcon: {
      '& path': {
        fill: primary.bluish7,
      },
    },
    answerText: {
      flexGrow: 1,
      minWidth: 0,
      fontSize: 14,
      lineHeight: '16px',
      alignSelf: 'center',
      wordWrap: 'break-word',
      [breakpoints.up('sm')]: {
        fontSize: 16,
        lineHeight: '18px',
      },
    },
  })
);

const MultiSelect = ({
  subjects = [],
  questionId = 0,
  type = '',
  option,
  selection = {},
  isDisabled = false,
  isSingleReview = false,
  onSelect = () => {},
}) => {
  const classes = useStyles();

  const [subject] = subjects;
  const isSelected = isQuestionnaireQuestionOptionSelectedCheck(
    selection,
    questionId,
    option.id,
    subject
  );

  const isGroupReview =
    !isSingleReview && isArray(subjects) && !isArrayEmpty(subjects);

  const handleSingleSelect = useCallback(() => {
    if (isSingleReview && !isDisabled) {
      onSelect(questionId, type, option.id, subject, isSelected);
    }
  }, [
    isDisabled,
    isSingleReview,
    isSelected,
    questionId,
    type,
    option.id,
    subject,
    onSelect,
  ]);

  const handleMultipleSelect = useCallback(
    (user, shouldDeleteAnswer) => {
      if (!isDisabled) {
        onSelect(questionId, type, option.id, user, shouldDeleteAnswer);
      }
    },
    [isDisabled, questionId, type, option.id, onSelect]
  );

  const isSubjectSelectedCheck = useCallback(
    user =>
      isQuestionnaireQuestionOptionSelectedCheck(
        selection,
        questionId,
        option.id,
        user
      ),
    [selection, questionId, option.id]
  );

  const isSubjectVisible = useCallback(
    user =>
      shouldRenderQuestionnaireQuestionOptionSubject(
        questionId,
        type,
        option.id,
        selection,
        user
      ),
    [questionId, type, option.id, selection]
  );

  return (
    <div className={classes.root}>
      <div
        className={classNames(classes.answerContent, {
          [classes.singleSelect]: isSingleReview,
          [classes.singleSelectDisabled]: isDisabled,
          selected: isSingleReview && isSelected,
        })}
        onClick={handleSingleSelect}
      >
        <div
          className={classNames(classes.answerWrapper, {
            [classes.answerWrapperGroup]: isGroupReview,
          })}
        >
          {isSingleReview && (
            <Checkbox
              classes={{
                root: classes.checkbox,
              }}
              icon={<span className={classes.checkboxIcon} />}
              checkedIcon={
                <CheckedIcon
                  className={classNames(classes.checkedIcon, {
                    [classes.disabledCheckedIcon]: isDisabled,
                  })}
                />
              }
              checked={!!isSelected}
              disabled={isDisabled}
              disableRipple
            />
          )}
          <RichTextPreview
            text={option.text}
            className={classes.answerText}
            variant="body1"
          />
        </div>
        {isGroupReview && (
          <SelectableUserList
            questionId={questionId}
            users={subjects}
            selection={selection}
            shouldRenderUser={isSubjectVisible}
            isDisabled={isDisabled}
            isUserSelectedCheck={isSubjectSelectedCheck}
            onSelect={handleMultipleSelect}
          />
        )}
      </div>
    </div>
  );
};

MultiSelect.propTypes = {
  option: PropTypes.shape({}).isRequired,
  subjects: PropTypes.arrayOf(PropTypes.shape({})),
  questionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  type: PropTypes.string,
  selection: PropTypes.shape({}),
  isSingleReview: PropTypes.bool,
  isDisabled: PropTypes.bool,
  onSelect: PropTypes.func,
};

export default MultiSelect;
